const ROUTE_VARIANT_STORAGE_PREFIX = 'routevariant-';
const LEGACY_SHIFT_STORAGE_PREFIX = 'shiftstations-';

const ROUTE_VARIANT_MODES = new Set([
  'default',
  'shift',
  'reverse_order',
  'reverse_stations',
  'start_end_switch',
]);

function parseInteger(value) {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function normalizeMode(mode, fallbackMode = 'default') {
  if (typeof mode !== 'string' || mode.length === 0) {
    return fallbackMode;
  }

  const trimmedMode = mode.trim();
  return ROUTE_VARIANT_MODES.has(trimmedMode) ? trimmedMode : fallbackMode;
}

function normalizeShift(shift, stationCount = 0) {
  const parsedShift = parseInteger(shift);

  if (stationCount <= 0) {
    return parsedShift;
  }

  return ((parsedShift % stationCount) + stationCount) % stationCount;
}

function rotateArray(values, amount) {
  if (!Array.isArray(values) || values.length === 0) {
    return values;
  }

  const normalizedAmount = normalizeShift(amount, values.length);
  if (normalizedAmount === 0) {
    return [...values];
  }

  return [...values.slice(normalizedAmount), ...values.slice(0, normalizedAmount)];
}

function remapStationPositions(stations, nextPositions) {
  return stations.map((station, index) => ({
    ...station,
    lat: nextPositions[index].lat,
    long: nextPositions[index].long,
  }));
}

export function getTrailRouteVariantStorageKey(trailId) {
  return `${ROUTE_VARIANT_STORAGE_PREFIX}${trailId}`;
}

export function getLegacyShiftStorageKey(trailId) {
  return `${LEGACY_SHIFT_STORAGE_PREFIX}${trailId}`;
}

export function normalizeTrailRouteVariant(variant) {
  if (variant == null) {
    return { mode: 'default', shift: 0 };
  }

  if (typeof variant === 'number' || typeof variant === 'string') {
    return {
      mode: 'shift',
      shift: parseInteger(variant),
    };
  }

  const detectedMode = normalizeMode(
    variant.type || variant.mode,
    variant.shift != null ? 'shift' : 'default'
  );

  return {
    mode: detectedMode,
    shift: detectedMode === 'shift' ? parseInteger(variant.shift) : 0,
  };
}

export function getTrailRouteVariantFromStartpoint(startpoint) {
  if (!startpoint) {
    return normalizeTrailRouteVariant(null);
  }

  return normalizeTrailRouteVariant(startpoint);
}

export function saveTrailRouteVariant(trailId, variant) {
  if (typeof localStorage === 'undefined') {
    return normalizeTrailRouteVariant(variant);
  }

  const normalizedVariant = normalizeTrailRouteVariant(variant);

  if (normalizedVariant.mode === 'default') {
    localStorage.removeItem(getTrailRouteVariantStorageKey(trailId));
    localStorage.removeItem(getLegacyShiftStorageKey(trailId));
    return normalizedVariant;
  }

  localStorage.setItem(getTrailRouteVariantStorageKey(trailId), JSON.stringify(normalizedVariant));

  if (normalizedVariant.mode === 'shift') {
    localStorage.setItem(getLegacyShiftStorageKey(trailId), `${normalizedVariant.shift}`);
  } else {
    localStorage.removeItem(getLegacyShiftStorageKey(trailId));
  }

  return normalizedVariant;
}

export function readTrailRouteVariant(trailId) {
  if (typeof localStorage === 'undefined') {
    return normalizeTrailRouteVariant(null);
  }

  const storedVariant = localStorage.getItem(getTrailRouteVariantStorageKey(trailId));
  if (storedVariant != null) {
    try {
      return normalizeTrailRouteVariant(JSON.parse(storedVariant));
    } catch (error) {
      return normalizeTrailRouteVariant(storedVariant);
    }
  }

  const legacyShift = localStorage.getItem(getLegacyShiftStorageKey(trailId));
  if (legacyShift != null) {
    return normalizeTrailRouteVariant({ mode: 'shift', shift: legacyShift });
  }

  return normalizeTrailRouteVariant(null);
}

export function trailRouteVariantsEqual(firstVariant, secondVariant) {
  const normalizedFirst = normalizeTrailRouteVariant(firstVariant);
  const normalizedSecond = normalizeTrailRouteVariant(secondVariant);

  return normalizedFirst.mode === normalizedSecond.mode && normalizedFirst.shift === normalizedSecond.shift;
}

export function applyTrailRouteVariant(stations, variant) {
  if (!Array.isArray(stations) || stations.length === 0) {
    return stations;
  }

  const normalizedVariant = normalizeTrailRouteVariant(variant);
  const positions = stations.map(station => ({ lat: station.lat, long: station.long }));

  switch (normalizedVariant.mode) {
    case 'shift': {
      const shiftedPositions = rotateArray(positions, normalizedVariant.shift);
      return remapStationPositions(stations, shiftedPositions);
    }
    case 'reverse_order':
      return [...stations].reverse();
    case 'reverse_stations': {
      const reversedPositions = [...positions].reverse();
      return remapStationPositions(stations, reversedPositions);
    }
    case 'start_end_switch': {
      if (stations.length <= 2) {
        return remapStationPositions([...stations], [...positions].reverse());
      }

      const switchedContentOrder = [
        stations[0],
        ...stations.slice(1, -1).reverse(),
        stations[stations.length - 1],
      ];

      return remapStationPositions(switchedContentOrder, [...positions].reverse());
    }
    default:
      return stations;
  }
}
